import React, { useEffect, useRef, useState } from 'react';
import { Upload, X, Image as ImageIcon, FolderOpen, Check } from 'lucide-react';
import { api, API_BASE } from '../../lib/api';

/**
 * Reusable image uploader. value: relative URL (e.g. /api/uploads/abc.png) or absolute URL or empty.
 *
 * Extensión V16.1 — botón "Biblioteca" que abre un picker modal listando
 * los assets del Media Library ya subidos.  El admin puede elegir uno
 * sin re-subirlo, evitando duplicados.
 */
export default function ImageUpload({ value, onChange, label, hint, testId = 'image-upload' }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  const display = value
    ? (value.startsWith('http') ? value : `${API_BASE}${value}`)
    : null;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange?.(data.url);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Error al subir');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  return (
    <div data-testid={testId}>
      {label && <label className="text-eyebrow mb-2 block">{label}</label>}
      <div className="flex flex-col gap-2">
        {display ? (
          <div className="relative w-full max-w-sm aspect-video bg-bg-surface border border-line rounded-md overflow-hidden">
            <img src={display} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange?.('')}
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-bg/80 border border-line text-ink hover:text-danger flex items-center justify-center"
              data-testid={`${testId}-remove`}
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex items-center justify-center gap-2 w-full max-w-sm aspect-video border border-dashed border-line-strong rounded-md text-ink-muted hover:text-acid hover:border-acid/40 transition"
            data-testid={`${testId}-trigger`}
          >
            <ImageIcon size={20} />
            <span className="text-sm font-mono">Subir imagen</span>
          </button>
        )}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder="…o pega una URL"
            className="flex-1 input-bare !py-2 !text-xs"
            data-testid={`${testId}-url`}
          />
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="px-3 py-2 text-xs font-mono uppercase tracking-[0.18em] border border-line-strong rounded-full text-ink-muted hover:text-acid hover:border-acid"
            data-testid={`${testId}-library`}
            title="Elegir desde Media Library"
          >
            <FolderOpen size={12} className="inline mr-1" /> Biblioteca
          </button>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="px-4 py-2 text-xs font-mono uppercase tracking-[0.18em] border border-line-strong rounded-full text-ink-muted hover:text-acid hover:border-acid disabled:opacity-50"
            data-testid={`${testId}-upload`}
          >
            <Upload size={12} className="inline mr-1" /> {busy ? '…' : 'Subir'}
          </button>
        </div>
        {hint && <p className="text-xs text-ink-dim">{hint}</p>}
        {err && <p className="text-xs text-danger">{String(err)}</p>}
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
      </div>

      {pickerOpen && (
        <LibraryPicker
          currentUrl={value || ''}
          onPick={(url) => { onChange?.(url); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
          testId={`${testId}-picker`}
        />
      )}
    </div>
  );
}


/* ====================================================================== */
/* Library picker modal                                                   */
/* ====================================================================== */
function LibraryPicker({ currentUrl, onPick, onClose, testId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/admin/media', { params: { kind: 'image', q } })
      .then(({ data }) => { if (!cancelled) setItems(data.items || []); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [q]);

  return (
    <div
      role="dialog"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      data-testid={testId}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-bg-surface border border-line max-w-4xl w-full max-h-[85vh] flex flex-col"
      >
        <div className="p-4 border-b border-line flex items-center gap-3">
          <span className="font-mono text-sm text-ink">Media Library</span>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar…"
            className="flex-1 bg-bg border border-line px-3 py-1.5 text-xs font-mono text-ink focus:border-acid focus:outline-none"
            data-testid={`${testId}-search`}
          />
          <button
            onClick={onClose}
            className="p-2 text-ink-muted hover:text-ink"
            data-testid={`${testId}-close`}
          >
            <X size={14} />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-ink-muted text-sm">Cargando…</div>
          ) : items.length === 0 ? (
            <div className="text-ink-muted text-sm">Sin medios · usa el botón "Subir" para crear uno.</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {items.map((a) => {
                const selected = (currentUrl || '').endsWith(a.url);
                const thumb = a.thumb_url ? `${API_BASE}${a.thumb_url}` : `${API_BASE}${a.url}`;
                return (
                  <button
                    type="button"
                    key={a.id}
                    onClick={() => onPick(a.url)}
                    className={`relative aspect-square border overflow-hidden group ${
                      selected ? 'border-acid' : 'border-line hover:border-acid/50'
                    }`}
                    data-testid={`${testId}-item-${a.id}`}
                    title={a.original_name}
                  >
                    <img src={thumb} alt={a.original_name} className="w-full h-full object-cover" />
                    {selected && (
                      <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-acid text-black flex items-center justify-center">
                        <Check size={10} />
                      </span>
                    )}
                    <span className="absolute inset-x-0 bottom-0 bg-bg/80 text-[10px] font-mono text-ink-muted px-1 py-0.5 truncate">
                      {a.original_name}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
